// Copyright (c) 2026 by Audere

import { Box, Stack, Typography } from "@mui/material";
import {
  CredentialResponse,
  GoogleLogin,
  GoogleOAuthProvider,
} from "@react-oauth/google";
import React, { useCallback, useEffect, useState } from "react";
import { createStyleSheet, globalStyles, logoColors } from "../utils/styles";

import AppLogo from "../../assets/audere-africa-logo.png";
import { State as AuthState } from "../features/auth/authSlice";
import { WarningAlert } from "../components/Alerts";
import { selectCurrentToken } from "../features/auth/authSlice";
import { setCredentials } from "../features/auth/authSlice";
import { useAppDispatch } from "../features/store";
import { useLazyOpenIdAuthenticateQuery } from "../features/auth/authApiSlice";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";

interface LocationState {
  from?: { pathname: string };
}

const getGoogleClientID = () => {
  const host = window.location.hostname;
  // TODO: configure the same client ID as ficus-portal. 
  const isStaging = host.includes(".staging.") || host.includes("localhost");
  return isStaging
    ? "12563075976-d6un192skhooj0cm85o9h6cbefifrpvu.apps.googleusercontent.com"
    : "41723139785-0641qoagcfpmiskspn57s8m17818gks3.apps.googleusercontent.com";
};

const Login: React.FC = () => {
  const [errorMsg, setErrorMsg] = useState("");
  const [welcomeMsg, setWelcomeMsg] = useState("Welcome");
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const clientId = getGoogleClientID();
  const isLoggedIn = !!useSelector(selectCurrentToken);

  const locationState = useLocation().state as LocationState | undefined;
  const destination = locationState?.from?.pathname ?? "/home";

  const [openIdAuthenticate, { data: authData, isError, error }] =
    useLazyOpenIdAuthenticateQuery();

  useEffect(() => {
    const isReturningUser = localStorage.getItem("isReturningUser");
    if (isReturningUser) setWelcomeMsg("Welcome back!");
    if (isLoggedIn) navigate(destination, { replace: true });
  }, [isLoggedIn, navigate, destination]);

  const loginSuccess = useCallback(
    (authState: AuthState) => {
      dispatch(setCredentials(authState));
      localStorage.setItem("isReturningUser", "true");
    },
    [dispatch]
  );

  useEffect(() => {
    if (authData) {
      loginSuccess(authData);
    } else if (isError) {
      setErrorMsg(
        error && "status" in error ? String(error.status) : "Login failed"
      );
    }
  }, [authData, isError, error, loginSuccess]);

  const onGoogleSuccess = useCallback(
    (credentialResponse: CredentialResponse) => {
      openIdAuthenticate(credentialResponse.credential!);
    },
    [openIdAuthenticate]
  );

  const onGoogleError = useCallback(() => {
    setErrorMsg("Google login failed");
  }, []);

  return (
    <GoogleOAuthProvider clientId={clientId}>
      <Box sx={globalStyles.pageContainerRow}>
        {/* Left pane with title */}
        <Box sx={styles.leftPane}>
          <Box textAlign="center">
            <Typography variant="h2" sx={styles.title}>
              Ficus Manager
            </Typography>
          </Box>
        </Box>

        {/* Right pane with login form */}
        <Box sx={styles.rightPane}>
          {/* Logo */}
          <Box marginBottom={2}>
            <img src={AppLogo} alt="Audere Africa logo" style={{ height: 60 }} />
          </Box>

          {/* Greeting and login */}
          <Stack spacing={2} alignItems="center">
            <Typography variant="h3">{welcomeMsg}</Typography>
            <Typography>Please sign in with Google to continue.</Typography>
            <GoogleLogin
              onSuccess={onGoogleSuccess}
              onError={onGoogleError}
              size="large"
              theme="filled_blue"
            />
            {errorMsg && <WarningAlert message={errorMsg} />}
          </Stack>
        </Box>
      </Box>
    </GoogleOAuthProvider>
  );
};

const styles = createStyleSheet({
  leftPane: {
    alignItems: "center",
    backgroundColor: logoColors.continent,
    display: "flex",
    flex: 7,
    justifyContent: "center",
  },
  title: {
    borderBottom: "2px solid white",
    color: "white",
    paddingBottom: 1,
  },
  rightPane: {
    ...globalStyles.flexColumn,
    alignItems: "center",
    flex: 5,
    justifyContent: "center",
    padding: 4,
  },
});

export default Login;
